import type { CSSProperties, FC, KeyboardEvent, ReactNode } from 'react';
import { useState } from 'react';
import { Card, Col, Row } from 'antd';
import { TbCircleCheckFilled } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OverflowFadeText from '@/shared/components/layouts/OverflowFadeText';
import { SmallTag } from '@/shared/components/tags';

interface SelectableOptionCardProps {
    borderStyle?: CSSProperties['borderStyle'];
    description: ReactNode;
    disabled?: boolean;
    icon: ReactNode;
    label: ReactNode;
    onSelect: () => void;
    selected: boolean;
    style?: CSSProperties;
}

const SelectableOptionCard: FC<SelectableOptionCardProps> = ({
    borderStyle = 'solid',
    description,
    disabled = false,
    icon,
    label,
    onSelect,
    selected,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const [hovered, setHovered] = useState(false);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
        }

        event.preventDefault();
        onSelect();
    };

    return (
        <Card
            aria-checked={selected}
            aria-disabled={disabled}
            role='radio'
            tabIndex={disabled ? -1 : 0}
            styles={{ body: { minWidth: 0, overflow: 'hidden', padding: '1rem' } }}
            style={{
                backgroundColor: hovered && !disabled ? token.colorBgBase : undefined,
                borderStyle,
                cursor: disabled ? 'not-allowed' : 'pointer',
                height: '100%',
                opacity: disabled ? 0.55 : 1,
                width: '100%',
                ...style,
            }}
            onClick={disabled ? undefined : onSelect}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Row
                align='middle'
                gutter={[8, 0]}
                style={{ marginBottom: '0.25rem', minWidth: 0, width: '100%' }}
                wrap={false}
            >
                <Col flex='none'>{icon}</Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <OverflowFadeText title={typeof label === 'string' ? label : undefined}>{label}</OverflowFadeText>
                </Col>
                {selected ? (
                    <Col flex='none'>
                        <SmallTag
                            message={
                                <>
                                    <TbCircleCheckFilled style={{ marginRight: '0.25rem', verticalAlign: '-0.1rem' }} />
                                    Selected
                                </>
                            }
                        />
                    </Col>
                ) : null}
            </Row>
            <small
                style={{
                    color: token.colorTextTertiary,
                    display: 'block',
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                }}
            >
                {description}
            </small>
        </Card>
    );
};

export type { SelectableOptionCardProps };
export default SelectableOptionCard;
